import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  useTranslation,
  useLocalNotificationBanner,
} from "Shared/util/custom-hooks";
// @ts-ignore: No TypeScript support
import makeComponentTrashable from "trashable-react";
import {
  InvoiceConnectionResource,
  InvoiceDataResource,
  InvoiceDetailResource,
  InvoicePaymentResource,
  InvoiceTimelineResource,
} from "Shared/util/types";
import { fetchInvoiceDetails, fetchInvoicePdf } from "Shared/util/web-api";
import useBrandContext from "Contexts/brand-context";
import isValid from "date-fns/isValid";
import { translateColumns, funcFilterStatus } from "Shared/util/translations";
import { formatDate, formatAmount } from "Shared/util/formatters";
import { RadioGroup, RadioButton } from "Shared/components/radio";
import DataTable, { ColumnTypes } from "Shared/components/tables/data-table";
import InvoiceTimeline from "./invoice-timeline";
import Page from "Shared/components/page/page";
import Button from "Shared/components/button";
import TextLink from "Shared/components/text-link";
import styles from "./details-page.css";

const paymentColumns = [
  {
    title: "registeredDateTitle",
    key: "registeredDate",
    type: ColumnTypes.Date,
    formatter: (d: string) => formatDate(d),
  },
  {
    title: "amountTitle",
    key: "amount",
    type: ColumnTypes.Amount,
    formatter: (d: string) => formatAmount(d),
  },
  {
    title: "interestTitle",
    key: "interest",
    type: ColumnTypes.Amount,
    formatter: (d: string) => formatAmount(d),
  },
  {
    title: "costsTitle",
    key: "costs",
    type: ColumnTypes.Amount,
    formatter: (d: string) => formatAmount(d),
  },
  {
    title: "currencyCodeTitle",
    key: "currencyCode",
  },
  {
    title: "paymentMeansTitle",
    key: "paymentMeansIdTranslationTag",
  },
];

const downloadPdf = (base64String: string, filename: string) => {
  const binary = atob(base64String.replace(/\s/g, ""));
  const len = binary.length;
  const buffer = new ArrayBuffer(len);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < len; i += 1) {
    view[i] = binary.charCodeAt(i);
  }

  const tempElement = document.createElement("a");
  const blob = new Blob([view], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  tempElement.href = url;
  tempElement.setAttribute("download", `${filename || "invoice"}.pdf`);
  document.body.appendChild(tempElement);
  tempElement.click();
  document.body.removeChild(tempElement);
};

const getPaymentsRowLink = (row: any) => {
  if (row.paymentMeansId !== 1) return null; // Only 'Avräknade'
  if (row.invoiceGroupId > 0) return `/invoice-groups/${row.invoiceGroupId}`;
  if (row.depositionGroupId > 0)
    return `/deposition-groups/${row.depositionGroupId}`;
  return null;
};

const renderProperty = (label: string, value: any) => (
  <div className={styles.property}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

enum Tab {
  General = "General",
  Debtor = "Debtor",
}

type QueryParams = {
  id: string;
};

type Props = {
  registerPromise: (p: Promise<any>) => Promise<any>;
};

const DetailsPage = ({ registerPromise }: Props) => {
  const { id: currentInvoiceId } = useParams<QueryParams>();
  const [pageLoading, setPageLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(Tab.General);
  const [invoice, setInvoice] = useState<InvoiceDataResource>(
    {} as InvoiceDataResource
  );
  const [connectedInvoices, setConnectedInvoices] = useState<
    InvoiceConnectionResource[]
  >([]);
  const [timeline, setTimeline] = useState<InvoiceTimelineResource[]>([]);
  const [payments, setPayments] = useState<InvoicePaymentResource[]>([]);
  const [ocrVisible, setOcrVisible] = useState(false);
  const [isDownloadingFile, setIsDownloadingFile] = useState(false);
  const { brandData, loadingBrandData } = useBrandContext();
  const { NotificationBannerType, addLocalNotificationBanner } =
    useLocalNotificationBanner();
  const history = useHistory();
  const t = useTranslation((x) => x.invoiceDetailsPage);
  const tg = useTranslation((x) => x.global);
  const ts = useTranslation((x) => x.servicesMapOnId);
  const tt = useTranslation((x) => x.invoiceDetailsTable);
  const tp = useTranslation((x) => x.paymentMeansMapOnId);
  const ti = useTranslation((x) => x.invoiceStatus2MapOnId);
  const invoiceStatus = { ...t.invoice_status, ...ti };

  useEffect(() => {
    setPageLoading(true);
    registerPromise(fetchInvoiceDetails(parseInt(currentInvoiceId)))
      .then((result: InvoiceDetailResource) => {
        setInvoice(result.data);
        setConnectedInvoices(result.connectedInvoices);
        setTimeline(result.timeline);
        setPayments(result.payments);
        setPageLoading(false);
      })
      .catch(() =>
        addLocalNotificationBanner(
          (tnb) => tnb.a_technical_error_occurred,
          NotificationBannerType.Error
        )
      );
  }, [currentInvoiceId]);

  useEffect(() => {
    if (!loadingBrandData) {
      setOcrVisible(brandData.showOCRNumber);
    }
  }, [loadingBrandData]);

  const handleRadioGroupChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setActiveTab(Tab[e.target.value as keyof typeof Tab]);

  const handleDownloadPdf = () => {
    setIsDownloadingFile(true);
    registerPromise(fetchInvoicePdf(invoice.sequenceNumber))
      .then((base64String) =>
        downloadPdf(base64String, invoice.sequenceNumber.toString())
      )
      .finally(() => setIsDownloadingFile(false));
  };

  const createConnectedInvoicesLinks = () =>
    connectedInvoices.map((invoice, index) => (
      <React.Fragment key={invoice.invoiceId}>
        {!!index && ", "}
        <TextLink
          text={invoice.invoiceNumber}
          link={() => history.push(`/invoices/${invoice.invoiceId}`)}
        />
      </React.Fragment>
    ));

  const isMonitored = invoice.serviceId === 2;
  const showTimeline = !invoice.isCreditInvoice;
  const showConnectedInvoices =
    Array.isArray(connectedInvoices) && connectedInvoices.length > 0;

  return (
    <Page narrowWidth title={t.invoiceSpecification} loading={pageLoading}>
      <div className={styles.container}>
        <div>
          <RadioGroup
            name="details-group"
            value={activeTab}
            handleChange={handleRadioGroupChange}
          >
            <RadioButton value={Tab.General} text={t.general} />
            <RadioButton
              value={Tab.Debtor}
              text={invoice.branch || invoice.debtor || ""}
            />
          </RadioGroup>
        </div>

        <div>
          <div className={styles.section}>
            {activeTab === Tab.General ? (
              <>
                <div className={styles.group}>
                  {renderProperty(tg.invoiceNumber, invoice.number)}
                  {ocrVisible &&
                    renderProperty(tg.ocrNumber, invoice.ocrNumber)}
                  {!ocrVisible &&
                    renderProperty(tg.reference_number, invoice.sequenceNumber)}
                  {showConnectedInvoices &&
                    renderProperty(
                      invoice.isCreditInvoice
                        ? t.credits_invoice
                        : t.used_credit_invoice,
                      createConnectedInvoicesLinks()
                    )}
                  {renderProperty(
                    tg.service,
                    ts[invoice.serviceIdTranslationTag]
                  )}
                  {isMonitored &&
                    renderProperty(
                      t.monitoringReason,
                      invoice.monitoringReason || "-"
                    )}
                  {renderProperty(
                    tg.status,
                    invoiceStatus[invoice.status] +
                      (isValid(new Date(invoice.statusDate))
                        ? ` ${formatDate(invoice.statusDate)}`
                        : ` ${invoice.statusDate}`)
                  )}
                </div>
                <div className={styles.group}>
                  {renderProperty(
                    tg.totalAmount,
                    `${formatAmount(invoice.totalAmount)} ${
                      invoice.currencyCode
                    }`
                  )}
                  {renderProperty(
                    t.paidAmount,
                    `${formatAmount(invoice.payedAmount)} ${
                      invoice.currencyCode
                    }`
                  )}
                  {renderProperty(
                    tg.balance,
                    `${formatAmount(invoice.remainingAmount)} ${
                      invoice.currencyCode
                    }`
                  )}
                </div>
                <div className={styles.group}>
                  {!isMonitored &&
                    renderProperty(
                      t.amountPurchased,
                      `${formatAmount(invoice.defaultTotalAmount)} ${
                        invoice.defaultCurrencyCode
                      }`
                    )}
                  {!isMonitored &&
                    renderProperty(
                      t.share,
                      `${formatAmount(invoice.buyingPercentage)} %`
                    )}
                  {renderProperty(
                    t.rateOfExchange,
                    formatAmount(invoice.buyingRate, 4)
                  )}
                </div>
              </>
            ) : (
              <>
                <div className={styles.group}>
                  {renderProperty(t.orgNumber, invoice.organizationNumber)}
                  {renderProperty(
                    t.address,
                    invoice.branchAddress || invoice.debtorAddress
                  )}
                  {renderProperty(
                    t.coAddress,
                    invoice.branchCOAddress || invoice.debtorCOAddress
                  )}
                  {renderProperty(
                    t.zipCode,
                    invoice.branchZipCode || invoice.debtorZipCode
                  )}
                  {renderProperty(
                    t.city,
                    invoice.branchCity || invoice.debtorCity
                  )}
                  {renderProperty(
                    t.country,
                    invoice.branchCountry || invoice.debtorCountry
                  )}
                </div>
                <div className={styles.group}>
                  <div className={styles.buttonDebtorContainer}>
                    <Button
                      className={styles.button}
                      text={t.showCustomer}
                      onClick={() =>
                        history.push(`/customers/${invoice.debtorId}`)
                      }
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className={styles.buttonsContainer}>
            {invoice.statusId === 8 && invoice.invoiceGroupId > 0 && (
              <div className={styles.buttonContainer}>
                <Button
                  buttonType="button-secondary"
                  text={t.showSettlementNote}
                  onClick={() =>
                    history.push(`/invoice-groups/${invoice.invoiceGroupId}`)
                  }
                />
              </div>
            )}
            {invoice.fileExists && (
              <div className={styles.buttonContainer}>
                <Button
                  buttonType="button-secondary"
                  text={t.downloadInvoiceCopy}
                  onClick={handleDownloadPdf}
                  isLoading={isDownloadingFile}
                />
              </div>
            )}
          </div>
        </div>

        <div>
          {showTimeline && (
            <InvoiceTimeline
              points={funcFilterStatus(timeline, t.invoice_status, "key")}
            />
          )}
        </div>

        <div>
          <h4>{t.payments}</h4>
          <DataTable
            columns={translateColumns(paymentColumns, tt)}
            rows={funcFilterStatus(
              payments,
              tp,
              "paymentMeansIdTranslationTag"
            )}
            getRowLink={getPaymentsRowLink}
            sorting={false}
            paging={false}
          />
        </div>
      </div>
    </Page>
  );
};

export default makeComponentTrashable(DetailsPage);
